import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LiliButton } from '@lili/lili-frontend-common';
import { useStore } from '../../../store';
import { SCHEDULE_C_DATA } from '../constants';
import { prettify } from '../helpers';
import { IIncomeData, IScheduleC } from '../../../types';
import { useDashboardDataQuery } from '../../../hooks/query/useDashboardDataQuery';
import Icon from '../../../generic-components/Icon';
import { donateIcon, scheduleCStep1 } from '../../../assets/svg';
import Button from '../../../generic-components/Button';
import TooltipWithTriangle from '../../../generic-components/TooltipWithTriangle';
import infoIcon from '../../../assets/svg/info-icon.svg';
import InfoPopup from '../../dashboard/components/InfoPopup';
import Lottie from '../../../generic-components/Lottie';
import CheckSignLottie from '../../../assets/lotties/checkmark_success_animation.json';
import Spinner, { SpinnerColor } from '../../../generic-components/Spinner';
import { useGenerateScheduleCFormQuery } from '../../../hooks/query/useGenerateScheduleCFormQuery';
import { convertValueToUSDv2 } from '../../../helpers/currency.helper';
import { CategorizeContainer } from '../../categorizeTransacrions/CategorizeContainer';
import { useGetUncategorizedTotalNumOfRecords } from '../../../hooks/query/useGetUncategorizedTransactionsDataForScheduleCQuery';
import { featureAccess, LimitedFeatureType } from '../../../services/appAccess.service';
import { FEATURE_FLAG } from '../../../constants/general';
import { FeatureAccess } from '../../../pages/AccountSettings/components/rolesAndPermissions/PermissionsList';

interface IProps {
  scheduleObject: IScheduleC;
  stepper: any;
  incomeData: IIncomeData;
  finishSetUp: () => void;
  isFetchingData: boolean;
  isZeroState: boolean;
  toggleCategorizationState: () => void;
}

const ScheduleCSetUp = ({
  scheduleObject,
  stepper,
  incomeData,
  finishSetUp,
  isFetchingData,
  isZeroState,
  toggleCategorizationState,
}: IProps) => {
  const { t } = useTranslation();
  const { customerId } = useStore((state) => state.currUser());
  const summaryData = useStore((state) => state.summary);
  const INITIAL_INPUTS_STATE = {
    freelance: '0',
    payroll: '0',
  };
  const { STEPS, TEXTS } = SCHEDULE_C_DATA;
  const [inputsState, setInputsState] = useState(INITIAL_INPUTS_STATE);
  const { currentStep, setCurrentStep } = stepper;
  const [year, setYear] = useState<number>(0);
  const totalIncome = incomeData?.totalIncome || 0;
  const totalCharityDonations = incomeData?.totalCharityDonations || 0;
  const [isFreelanceTooltipShown, setIsFreelanceTooltipShown] = useState(false);
  const [isPayrollTooltipShown, setIsPayrollTooltipShown] = useState(false);
  const [showDonatesPopup, setShowDonatesPopup] = useState<boolean>(false);
  const [isCategorizeFinished, setIsCategorizeFinished] = useState<boolean>(false);
  const [toggleData, setToggleData] = useState<{ incomeTrxCnt: number; expenseTrxCnt: number }>();
  const getQueryStr = (type: string) => `&startTime=${year}-01-01&endTime=${year}-12-31&categorizationType=${type}`;
  const { mutateAsync: getUncategorizedTrxCount } = useGetUncategorizedTotalNumOfRecords();
  const categorizePermission = featureAccess(
    FEATURE_FLAG.CATEGORIZATION.FEATURE,
    LimitedFeatureType.TransactionListCategorize,
  );

  function getToggleData() {
    Promise.all([
      getUncategorizedTrxCount({ q: getQueryStr('INCOME'), customerId }),
      getUncategorizedTrxCount({ q: getQueryStr('EXPENSE'), customerId }),
    ]).then((values: [number, number]) => {
      const incomeTrxCnt = values[0];
      const expenseTrxCnt = values[1];
      if (!(incomeTrxCnt + expenseTrxCnt) && currentStep === STEPS.STEP_3) {
        setCurrentStep(STEPS.STEP_4);
      }
      setToggleData({ incomeTrxCnt, expenseTrxCnt });
    });
  }

  const shouldShowCharityPopup = () => {
    if (totalCharityDonations) {
      setShowDonatesPopup(true);
    }
  };

  const handleFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (!value) return;
    setTimeout(() => {
      event.target.selectionStart = value.length;
      event.target.selectionEnd = value.length;
    }, 50);
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.dataset.type;
    const { value } = event.target;
    const dotSeparatorsArr = value.split('').filter((i) => i === '.');
    let cleanValue = value.replace('$', '').replaceAll(',', '').replace(/\D/g, '');

    if (cleanValue.length > 1 && cleanValue.charAt(0) === '0') {
      cleanValue = cleanValue.substring(1);
    }

    if (dotSeparatorsArr.length > 1) {
      return;
    }

    if (cleanValue === '-' || cleanValue === '.' || !cleanValue) {
      cleanValue = '0';
    }

    if (Number(cleanValue) > totalIncome) {
      return;
    }

    if (key === 'payroll') {
      setInputsState({ payroll: cleanValue, freelance: JSON.stringify(totalIncome - Number(cleanValue)) });
    } else if (key === 'freelance') {
      setInputsState({ payroll: JSON.stringify(totalIncome - Number(cleanValue)), freelance: cleanValue });
    } else {
      setInputsState((prevState) => ({
        ...prevState,
        [key as string]: cleanValue,
      }));
    }
  };

  const freelanceValue = useMemo(() => {
    if (inputsState.freelance.slice(-1) === '.') {
      return `${convertValueToUSDv2(Number(inputsState.freelance))}.`;
    }
    return convertValueToUSDv2(Number(inputsState.freelance));
  }, [inputsState.freelance]);

  const payrollValue = useMemo(() => {
    if (inputsState.payroll.slice(-1) === '.') {
      return `${convertValueToUSDv2(Number(inputsState.payroll))}.`;
    }
    return convertValueToUSDv2(Number(inputsState.payroll));
  }, [inputsState.payroll]);

  const shouldAllowNextFromStepTwo = useMemo(() => {
    const { payroll, freelance } = inputsState;
    if (payroll.includes('-') || freelance.includes('-')) return false;
    if (payroll === '0' && freelance === '0') return false;
    return true;
  }, [inputsState.payroll, inputsState.freelance, currentStep]);

  const handleViewPdf = (data: string) => {
    if (data) {
      window.open(data, '_blank');
    }
  };

  const handleSetYear = () => {
    if (scheduleObject?.year) setYear(scheduleObject.year);
    setInputsState(INITIAL_INPUTS_STATE);
    setIsCategorizeFinished(false);
  };

  const handleFinishCategorize = () => {
    setCurrentStep(STEPS.STEP_4);
    setIsCategorizeFinished(true);
    toggleCategorizationState();
  };

  const { refetch: generateForm, isLoading: isGeneratingScheduleCForm } = useGenerateScheduleCFormQuery({
    customerId: scheduleObject.customerId,
    scheduleCId: scheduleObject.scheduleCId,
    year,
    totalIncome,
    salary: parseFloat(inputsState.payroll),
    freelanceIncome: parseFloat(inputsState.freelance),
    charityDonations: totalCharityDonations,
    onSuccess: (data: string) => {
      handleViewPdf(data);
      finishSetUp();
    },
  });

  const { data, isFetched: isDashboardDataFetchedSuccessfully } = useDashboardDataQuery({
    customerId,
    disableSelfUpdate: false,
  });
  const setCurrentDashboardData = useStore((state) => state.setCurrentDashboardData);

  useEffect(() => {
    if (currentStep === STEPS.STEP_1 || currentStep === STEPS.STEP_2) {
      handleSetYear();
    }
    if (currentStep === STEPS.STEP_4) {
      shouldShowCharityPopup();
    }
  }, [currentStep, scheduleObject]);

  useEffect(() => {
    if (data && isDashboardDataFetchedSuccessfully) {
      const {
        uncategorizedTransactions: { transactionList: uncategorizedTransactionList, uncategorizedTotalNumOfRecords },
        taxRequestResponse: {
          taxEstimate: { taxSaving },
        },
        latestTransactions: { transactionList: latestTransactions },
      } = data;
      const newDashboardData = {
        uncategorizedTransactionList,
        uncategorizedTotalNumOfRecords,
        taxSaving,
        latestTransactions,
      };
      setCurrentDashboardData(newDashboardData);
    }
  }, [isDashboardDataFetchedSuccessfully]);

  useEffect(() => {
    if (!year) return;
    getToggleData();
  }, [year]);

  return isFetchingData ? (
    <div className='w-[456px] h-[548px] px-[40px] pt-[40px] rounded-lg flex items-center justify-center bg-bw00'>
      <Spinner spinnerColor={SpinnerColor.Blue} height='2rem' width='2rem' />
    </div>
  ) : isZeroState ? (
    <div
      id='noReportsYet'
      className='w-[456px] h-[548px] px-[40px] pt-[40px] rounded-lg flex items-center justify-center bg-bw00'
    >
      There are no reports yet
    </div>
  ) : (
    <div
      className={`w-[456px] h-[575px] ${currentStep === STEPS.STEP_3 ? '' : 'px-[40px] py-[40px]'} rounded-lg flex flex-col items-center justify-center bg-bw00`}
    >
      {currentStep === STEPS.STEP_1 && (
        <>
          <Icon className='w-[120px] h-[120px] mb-[11px] self-start' src={scheduleCStep1} />
          <span className='h-[34px] mb-[24px] inline-flex items-center font-metricMedium text-[20px] text-bw08'>
            {t('SCHEDULE_C_SETUP_STEP_1_TITLE')}
          </span>
          <div className='w-[23.5rem] h-[14.875rem] bg-bw02 pl-[20px] pt-[20px] pb-[16px] mb-[24px] flex flex-col rounded-lg'>
            <span className='h-[24px] font-metricMedium text-bw05'>{t('SCHEDULE_C_SETUP_STEP_1_DISCLAIMER')}</span>
            <div className='w-[21rem] border border-bw05 border-b mb-[8px] opacity-30' />
            <span className='h-[168px] w-full pr-[50px] overflow-scroll text-bw07 font-metric text-base whitespace-pre-line'>
              {t('SCHEDULE_C_SETUP_STEP_1_TEXT')}
              <a target='_blank' className='hover:text-action03' href={TEXTS.STEP_1.TEXT_LINK} rel='noreferrer'>
                {TEXTS.STEP_1.TEXT_LINK}
              </a>
            </span>
          </div>
          <Button
            onClick={() => {
              setCurrentStep(currentStep + 1);
            }}
            title={t('SCHEDULE_C_SETUP_STEP_1_BUTTON')}
            className='w-[15rem] h-[3.125rem] rounded-umd bg-action03 hover:bg-action04 text-sm font-metricSemibold text-bw00'
            id='getStartedButton'
          />
        </>
      )}
      {currentStep === STEPS.STEP_2 && (
        <>
          <span className='h-[40px] inline-flex justify-center items-center text-[32px] font-metricSemibold text-bw08'>
            {prettify(totalIncome)}
          </span>
          <span className='h-[19px] font-metric text-center text-bw07 text-base mt-[8px]'>
            {t('SCHEDULE_C_SETUP_STEP_2_UNDER_MONEY_TEXT')}
          </span>
          <div className='w-full border border-bw05 border-b mb-[8px] opacity-30 mt-[15px] mb-[24px]' />
          <div className='flex w-full justify-between mb-[24px]'>
            <div className='relative flex items-center gap-2'>
              {isFreelanceTooltipShown && (
                <TooltipWithTriangle
                  tooltipContainerClassName='absolute translate-x-[70%] translate-y-[-90%] top-0 right-0 flex flex-col justify-center'
                  tooltipContentClassName='w-[126px] h-[55px] text-xxs w-full font-metric text-bw07 rounded-md px-[6px] py-2 bg-[#F2F4F7] border border-bw05'
                  triangleClassName='mt-[-10px] ml-[20px]'
                  triangleClass='triangle-F2F4F7'
                >
                  <span className='w-full'>{t('SCHEDULE_C_SETUP_STEP_2_FREELANCE_TOOLTIP')}</span>
                </TooltipWithTriangle>
              )}
              <div className='overflow-hidden truncate max-w-[150px]'>
                <span className='font-metric text-base text-bw07'>
                  {summaryData?.dba?.name || t('SCHEDULE_C_SETUP_STEP_2_FREELANCE_HINT')}
                </span>
              </div>
              <Button
                handleMouseEnter={() => setIsFreelanceTooltipShown(true)}
                handleMouseLeave={() => setIsFreelanceTooltipShown(false)}
                className=''
                id='showTooltipFreelance'
                childElement={<Icon className='w-[14px] h-[14px]' src={infoIcon} />}
              />
            </div>
            <input
              data-type='freelance'
              placeholder='0'
              onFocus={handleFocus}
              onChange={handleInput}
              value={`$${freelanceValue}`}
              className='w-[176px] h-[40px] bg-[#F2F4F7] focus:border-action03 box-border border border-[#F2F4F7] outline-none px-[8px] rounded-md font-metricMedium text-sm text-bw08 text-end'
              type='text'
            />
          </div>
          <div className='w-full flex justify-between'>
            <div className='relative flex items-center gap-2'>
              {isPayrollTooltipShown && (
                <TooltipWithTriangle
                  tooltipContainerClassName='absolute translate-x-[70%] translate-y-[-90%] top-0 right-0 flex flex-col justify-center'
                  tooltipContentClassName='w-[117px] h-[75px] text-xxs w-full font-metric text-bw07 rounded-md px-[6px] py-2 bg-[#F2F4F7] border border-bw05'
                  triangleClassName='mt-[-10px] ml-[20px]'
                  triangleClass='triangle-F2F4F7'
                >
                  <span className='w-full whitespace-pre-line'>
                    {t('SCHEDULE_C_SETUP_STEP_2_OTHER_INCOME_TOOLTIP')}
                  </span>
                </TooltipWithTriangle>
              )}
              <div className='overflow-hidden truncate max-w-[150px]'>
                <span className='font-metric text-base text-bw07'>
                  {t('SCHEDULE_C_SETUP_STEP_2_OTHER_INCOME_HINT')}
                </span>
              </div>
              <Button
                handleMouseEnter={() => setIsPayrollTooltipShown(true)}
                handleMouseLeave={() => setIsPayrollTooltipShown(false)}
                className=''
                id='showTooltipPayroll'
                childElement={<Icon className='w-[14px] h-[14px]' src={infoIcon} />}
              />
            </div>
            <input
              data-type='payroll'
              onFocus={handleFocus}
              onChange={handleInput}
              value={`$${payrollValue}`}
              className='w-[176px] h-[40px] bg-[#F2F4F7] box-border border border-[#F2F4F7] focus:border-action03 outline-none px-[8px] rounded-md font-metricMedium text-sm text-bw08 text-end'
              type='text'
            />
          </div>
          <div className='w-full border border-bw05 border-b mb-[8px] opacity-30 mt-[24px] mb-[20px]' />
          <span className='h-[22px] text-action03 font-metricMedium text-[18px] mb-[8px] mr-auto'>
            {t('SCHEDULE_C_SETUP_STEP_2_SUB_TITLE')}
          </span>
          <span className='w-full h-[144px] font-metric text-base text-bw07'>{t('SCHEDULE_C_SETUP_STEP_2_TEXT')}</span>
          <Button
            disable={!shouldAllowNextFromStepTwo}
            onClick={() => {
              setCurrentStep(currentStep + 1);
            }}
            title={t('SCHEDULE_C_SETUP_STEP_2_BUTTON')}
            className='w-[15rem] h-[3.125rem] rounded-umd bg-action03 hover:bg-action04 disabled:bg-bw05 text-sm font-metricSemibold text-bw00 mt-[13px]'
            id='nextStepButton'
          />
        </>
      )}
      {currentStep === STEPS.STEP_3 && toggleData && (
        <>
          <CategorizeContainer
            isReadOnlyMode={categorizePermission !== FeatureAccess.READ_WRITE}
            handleFinishCategorize={handleFinishCategorize}
            containerClassName='w-[75%] h-full py-[0px] overflow-hidden flex flex-col items-center justify-center relative'
            filterYearValue={year}
            customMode='SC'
            toggleData={toggleData}
          />
          {isCategorizeFinished && (
            <Button
              onClick={() => setCurrentStep((prevState: number) => prevState + 1)}
              title={t('SCHEDULE_C_SETUP_STEP_2_BUTTON')}
              className='w-[240px] h-[50px] rounded-umd bg-action03 hover:bg-action04 disabled:bg-bw05 text-sm font-metricSemibold text-bw00 mb-[56px] self-center'
              id='GoNextStepButton'
            />
          )}
        </>
      )}
      {currentStep === STEPS.STEP_4 && (
        <>
          {showDonatesPopup && (
            <InfoPopup
              flexParams='flex flex-col items-center'
              popupHeight='h-[486px]'
              className='pt-[50px] pb-[70px] px-[34px]'
              handleState={() => {
                setShowDonatesPopup(false);
              }}
              additionalCallback={() => {
                setShowDonatesPopup(false);
              }}
              pictureSrc={donateIcon}
              pictureClassName='w-[76px] h-[76px] mb-[17px]'
              showInfoPopup={showDonatesPopup}
              text={`${t('SCHEDULE_C_SETUP_POPUP_TEXT_1')}${totalCharityDonations}${t('SCHEDULE_C_SETUP_POPUP_TEXT_2')}`}
              textClassName='h-[96] mb-[63px]'
              title={t('SCHEDULE_C_SETUP_POPUP_TITLE')}
              titleClassName='text-bw08 font-metric text-[24px] mb-[36px]'
              buttonClassName='w-[240px] h-[50px]'
              mainButtonText={t('MY_LILI_CARD_ERROR_MODAL_BTN')}
            />
          )}
          <Lottie lottieAsset={CheckSignLottie} isLoop={false} isAutoplay className='w-[69px] h-[69px]' />
          <span className='mt-[27px] h-[42px] inline-flex items-center text-[25px] font-metricSemibold text-bw08'>
            {t('SCHEDULE_C_SETUP_LAST_STEP_TITLE')}
          </span>
          <span className='h-[24px] font-metric text-base text-bw07 mb-[35px]'>
            {t('SCHEDULE_C_SETUP_LAST_STEP_SUB_TITLE')}
          </span>
          <span className='h-[144px] text-bw07 font-metric text-base mb-[77px]'>
            {t('SCHEDULE_C_SETUP_LAST_STEP_TEXT')}
          </span>
          <LiliButton
            id='GenerateSchedule-CForm'
            onClick={() => generateForm()}
            className='w-[240px]'
            title={t('SCHEDULE_C_SETUP_LAST_STEP_BUTTON')}
            isLoading={isGeneratingScheduleCForm}
            disabled={isGeneratingScheduleCForm}
          />
        </>
      )}
    </div>
  );
};

export default ScheduleCSetUp;
